import { useState } from "react";
import { Microscope, Sparkles, BookText, Ban, AlertTriangle, CheckCircle2, Lightbulb, ShieldAlert, HeartPulse } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CONDITIONS } from "@/data/conditions";
import { ALLERGIES } from "@/data/allergies";

const FoodList = ({ icon: Icon, title, items, color }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-white/5 bg-black/20 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color }}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span key={it} className="rounded-md px-2 py-1 text-xs" style={{ background: `${color}1a`, color }}>{it}</span>
        ))}
      </div>
    </div>
  );
};

const BulletList = ({ icon: Icon, title, items, color }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-sm font-bold" style={{ color }}>
        <Icon className="h-4 w-4" /> {title}
      </div>
      <ul className="space-y-2">
        {items.map((r) => (
          <li key={r} className="flex gap-2.5 text-sm leading-relaxed">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
            <span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Refs = ({ refs }) => (
  <div className="mt-4 border-t border-white/5 pt-3">
    <div className="field-label mb-2">Riferimenti scientifici (linee guida / PubMed)</div>
    <div className="flex flex-wrap gap-2">
      {refs.map((ref) => (
        <span key={ref} className="rounded-md border border-[hsl(var(--c-teal))]/30 bg-[hsl(var(--c-teal))]/10 px-2.5 py-1 text-xs text-[hsl(var(--c-teal))]">{ref}</span>
      ))}
    </div>
    <p className="mt-2 text-[11px] italic text-muted-foreground">Informazioni a scopo professionale, non sostituiscono la valutazione medica individuale.</p>
  </div>
);

export default function ClinicaTab({ clinica, setClinica }) {
  const [condResult, setCondResult] = useState(clinica.result || null);
  const [allergyResult, setAllergyResult] = useState(clinica.allergiaResult || null);

  const generateCondition = () => {
    const cond = CONDITIONS.find((c) => c.value === clinica.condizione);
    if (!cond) return;
    const res = { ...cond, note: clinica.note || "" };
    setCondResult(res);
    setClinica({ ...clinica, result: res });
  };

  const generateAllergy = () => {
    const a = ALLERGIES.find((x) => x.value === clinica.allergia);
    if (!a) return;
    setAllergyResult(a);
    setClinica({ ...clinica, allergiaResult: a });
  };

  return (
    <div className="space-y-6" data-testid="tab-clinica">
      {/* ---- Condizione clinica ---- */}
      <section className="panel p-5 sm:p-6">
        <h2 className="mb-5 flex items-center gap-2 font-display text-lg font-bold">
          <Microscope className="h-5 w-5 text-primary" /> Condizione Clinica & Raccomandazioni (Evidence-Based)
        </h2>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="field-label mb-1">Problematica del Soggetto</div>
            <Select value={clinica.condizione || ""} onValueChange={(v) => setClinica({ ...clinica, condizione: v })}>
              <SelectTrigger className="lab-input h-auto" data-testid="condition-select">
                <SelectValue placeholder="Seleziona condizione..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {CONDITIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="field-label mb-1">Note Cliniche (opzionale)</div>
            <input className="lab-input" placeholder="farmaci, obiettivi, dettagli..." value={clinica.note || ""} onChange={(e) => setClinica({ ...clinica, note: e.target.value })} data-testid="clinical-notes-input" />
          </div>
        </div>

        <button
          onClick={generateCondition}
          disabled={!clinica.condizione}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="generate-reco-btn"
        >
          <Sparkles className="h-4 w-4" /> Genera Raccomandazioni Scientifiche
        </button>

        {condResult && (
          <div className="mt-6 space-y-5" data-testid="reco-result">
            <h3 className="flex items-center gap-2 font-display text-base font-bold text-primary">
              <BookText className="h-4 w-4" /> {condResult.label}
            </h3>

            <BulletList icon={HeartPulse} title="Strategia dietetica" items={condResult.reco} color="hsl(var(--primary))" />

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <FoodList icon={Ban} title="Da escludere" items={condResult.escludere} color="hsl(0 72% 62%)" />
              <FoodList icon={AlertTriangle} title="Da limitare" items={condResult.limitare} color="hsl(var(--c-fat))" />
              <FoodList icon={CheckCircle2} title="Da preferire" items={condResult.preferire} color="hsl(var(--c-prot))" />
            </div>

            <BulletList icon={Lightbulb} title="Consigli certificati" items={condResult.consigli} color="hsl(var(--c-teal))" />

            {condResult.note && (
              <div className="rounded-lg border border-white/5 bg-black/20 px-4 py-3 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">Note: </span>{condResult.note}
              </div>
            )}

            <Refs refs={condResult.refs} />
          </div>
        )}
      </section>

      {/* ---- Allergie e intolleranze ---- */}
      <section className="panel p-5 sm:p-6">
        <h2 className="mb-5 flex items-center gap-2 font-display text-lg font-bold">
          <ShieldAlert className="h-5 w-5 text-[hsl(var(--c-fat))]" /> Allergie & Intolleranze
        </h2>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="field-label mb-1">Seleziona allergia / intolleranza</div>
            <Select value={clinica.allergia || ""} onValueChange={(v) => setClinica({ ...clinica, allergia: v })}>
              <SelectTrigger className="lab-input h-auto" data-testid="allergy-select">
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {ALLERGIES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <button
              onClick={generateAllergy}
              disabled={!clinica.allergia}
              className="inline-flex items-center gap-2 rounded-lg bg-[hsl(var(--c-fat))] px-4 py-2.5 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="generate-allergy-btn"
            >
              <Sparkles className="h-4 w-4" /> Genera Indicazioni
            </button>
          </div>
        </div>

        {allergyResult && (
          <div className="mt-6 space-y-5" data-testid="allergy-result">
            <div>
              <h3 className="flex items-center gap-2 font-display text-base font-bold text-[hsl(var(--c-fat))]">
                <BookText className="h-4 w-4" /> {allergyResult.label}
                <span className="rounded-md bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">{allergyResult.tipo}</span>
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">{allergyResult.descr}</p>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FoodList icon={Ban} title="Alimenti da escludere" items={allergyResult.escludere} color="hsl(0 72% 62%)" />
              <FoodList icon={AlertTriangle} title="Attenzione (fonti nascoste)" items={allergyResult.attenzione} color="hsl(var(--c-fat))" />
            </div>

            <BulletList icon={Lightbulb} title="Come trattare / consigli" items={allergyResult.consigli} color="hsl(var(--c-teal))" />

            <Refs refs={allergyResult.refs} />
          </div>
        )}
      </section>
    </div>
  );
}
